import type { PolyNoteEvent } from "../../hooks/usePolyPitchDetector";
import type { NoteName } from "../guitarEngine";
import type { ChordPathMission } from "./chordPathPlan";

export type StrumScoreResult = {
  passed: boolean;
  matchedCount: number;
  needed: number;
  matchedPcs: number[];
  missingPcs: number[];
  detectedPcs: number[];
};

function pcsFromEvents(events: PolyNoteEvent[]): Set<number> {
  const set = new Set<number>();
  for (const e of events) {
    set.add(((Math.round(e.pitch) % 12) + 12) % 12);
  }
  return set;
}

function hasMidiNear(events: PolyNoteEvent[], target: number, tol = 1): boolean {
  return events.some((e) => Math.abs(Math.round(e.pitch) - target) <= tol);
}

/**
 * Evalúa un rasgueo polifónico contra los criterios de la misión.
 */
export function scoreChordStrum(
  mission: ChordPathMission,
  events: PolyNoteEvent[]
): StrumScoreResult {
  const detectedPcs = Array.from(pcsFromEvents(events));

  if (mission.requiredMidis?.length) {
    const hits = mission.requiredMidis.filter((m) => hasMidiNear(events, m));
    const needed = mission.minHits;
    const passed = hits.length >= needed;
    const reqPcs =
      mission.requiredPitchClasses ??
      mission.requiredMidis.map((m) => ((m % 12) + 12) % 12);
    const det = pcsFromEvents(events);
    return {
      passed,
      matchedCount: hits.length,
      needed,
      matchedPcs: reqPcs.filter((pc) => det.has(pc)),
      missingPcs: reqPcs.filter((pc) => !det.has(pc)),
      detectedPcs,
    };
  }

  const required = mission.requiredPitchClasses ?? [];
  const det = pcsFromEvents(events);
  const matchedPcs = required.filter((pc) => det.has(pc));
  const missingPcs = required.filter((pc) => !det.has(pc));
  const needed = mission.minHits;
  return {
    passed: matchedPcs.length >= needed,
    matchedCount: matchedPcs.length,
    needed,
    matchedPcs,
    missingPcs,
    detectedPcs,
  };
}

export function noteOctaveMatches(
  heardNote: NoteName | null,
  heardOctave: number,
  expectedNote: NoteName,
  expectedOctave: number
): boolean {
  if (!heardNote) return false;
  return heardNote === expectedNote && heardOctave === expectedOctave;
}
